/**
 * Email Obfuscation Utility
 * 
 * Protects email addresses from email harvesting by replacing
 * @ with [at] and . with [dot]
 * Implements CWE-200 security requirement
 */

/**
 * Obfuscate an email address to prevent harvesting
 * Replaces @ with [at] and . with [dot]
 * Example: abc@nic.in becomes abc[at]nic[dot]in
 * 
 * @param {string} email - Email address to obfuscate
 * @returns {string} Obfuscated email address
 */
export const obfuscateEmail = (email) => {
  if (!email || typeof email !== 'string') {
    return email || '';
  }

  // Replace @ with [at] and . with [dot]
  return email
    .replace(/@/g, '[at]')
    .replace(/\./g, '[dot]');
};

/**
 * Deobfuscate an email address (for internal use only)
 * Replaces [at] with @ and [dot] with .
 * 
 * @param {string} obfuscatedEmail - Obfuscated email address
 * @returns {string} Original email address
 */
export const deobfuscateEmail = (obfuscatedEmail) => {
  if (!obfuscatedEmail || typeof obfuscatedEmail !== 'string') {
    return obfuscatedEmail || '';
  }

  // Replace [at] with @ and [dot] with .
  return obfuscatedEmail
    .replace(/\[at\]/g, '@')
    .replace(/\[dot\]/g, '.');
};

/**
 * Component to display obfuscated email address
 * Can be used as a React component for better UX
 * 
 * @param {Object} props
 * @param {string} props.email - Email address to display
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.showCopyButton - Whether to show copy button (optional)
 * @returns {JSX.Element} Obfuscated email display
 */
export const ObfuscatedEmail = ({ email, className = '', showCopyButton = false }) => {
  if (!email) return null;

  const obfuscated = obfuscateEmail(email);

  const handleCopy = () => {
    // Copy the original email (not obfuscated) to clipboard
    navigator.clipboard.writeText(email).then(() => {
      // You can add a toast notification here if needed
      console.log('Email copied to clipboard');
    }).catch(err => {
      console.error('Failed to copy email:', err);
    });
  };

  return (
    <span className={className}>
      {obfuscated}
      {showCopyButton && (
        <button
          onClick={handleCopy}
          className="ml-2 text-primary-600 hover:text-primary-700"
          aria-label="Copy email address"
          title="Copy email address"
        >
          <svg className="w-4 h-4 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
        </button>
      )}
    </span>
  );
};

export default obfuscateEmail;






